import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent, 
  DialogActions,
  Button,
  TextField,
  Box,
  Alert,
  InputAdornment,
  IconButton,
  Typography,
  CircularProgress,
} from "@mui/material";
import {
  useAddBankAccountMutation,
  useUpdateBankAccountMutation,
} from "../api/bankAccountApi";
import { PhotoCamera, QrCode } from "@mui/icons-material";

interface Props {
  open: boolean;
  setModalOpen: (open: boolean) => void;
  onSuccess?: () => void;
  account?: BankAccount | null;
}

const AddEditBankAccount: React.FC<Props> = ({ open, setModalOpen, onSuccess, account }) => {
  const [formData, setFormData] = useState<{
    bank_name: string;
    account_number: string;
    qr_code: File | null;
    existingQrCode?: string;
  }>({
    bank_name: "",
    account_number: "",
    qr_code: null,
    existingQrCode: undefined,
  });

  const [errors, setErrors] = useState({
    bank_name: false,
    account_number: false,
    qr_code: false,
  });

  const [addBankAccount] = useAddBankAccountMutation();
  const [updateBankAccount] = useUpdateBankAccountMutation();
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (account) {
      setFormData({
        bank_name: account.bank_name,
        account_number: account.account_number,
        qr_code: null,
        existingQrCode: account.qr_code || undefined,
      });
      // Reset errors when editing existing account
      setErrors({
        bank_name: false,
        account_number: false,
        qr_code: false,
      });
    } else {
      setFormData({
        bank_name: "",
        account_number: "",
        qr_code: null,
        existingQrCode: undefined,
      });
    }
  }, [account, open]);

  const handleQrCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setFormData((prev) => ({ ...prev, qr_code: file }));
    setErrors(prev => ({ ...prev, qr_code: false }));
  };

  const validateForm = () => {
    const newErrors = {
      bank_name: !formData.bank_name.trim(),
      account_number: !formData.account_number.trim(),
      qr_code: !formData.qr_code && !formData.existingQrCode,
    };
    setErrors(newErrors);
    return !Object.values(newErrors).some(error => error);
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    const fd = new FormData();
    fd.append("bank_name", formData.bank_name);
    fd.append("account_number", formData.account_number);
    if (formData.qr_code) {
      fd.append("qr_code", formData.qr_code);
    }

    try {
      if (account?.id) {
        await updateBankAccount({ id: account.id, body: fd }).unwrap();
      } else {
        await addBankAccount({ body: fd }).unwrap();
      }
      setModalOpen(false);
      onSuccess?.();
      setSubmissionError(null);
    } catch (err) {
      console.error("Error saving bank account:", err);
      setSubmissionError("Failed to save bank account. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => setModalOpen(false)} maxWidth="sm" fullWidth>
      <DialogTitle>{account ? "Edit Bank Account" : "Add Bank Account"}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            fullWidth
            margin="dense"
            label="Bank Name"
            value={formData.bank_name}
            onChange={(e) => {
              setFormData({ ...formData, bank_name: e.target.value });
              setErrors(prev => ({ ...prev, bank_name: false }));
            }}
            error={errors.bank_name}
            helperText={errors.bank_name ? "Bank name is required" : ""}
            required
          />

          <TextField
            fullWidth
            margin="dense"
            label="Account Number"
            value={formData.account_number}
            onChange={(e) => {
              setFormData({ ...formData, account_number: e.target.value });
              setErrors(prev => ({ ...prev, account_number: false }));
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <IconButton edge="start" disabled>
                    <QrCode />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            error={errors.account_number}
            helperText={errors.account_number ? "Account number is required" : ""}
            required
          />

          <Box sx={{ mt: 2 }}>
            <Button 
              variant="outlined" 
              component="label"
              startIcon={<PhotoCamera />}
              color={errors.qr_code ? "error" : "primary"}
            >
              Upload QR Code
              <input 
                hidden 
                accept="image/*" 
                type="file" 
                onChange={handleQrCodeChange} 
              />
            </Button>
            {errors.qr_code && (
              <Typography variant="caption" color="error" display="block" mt={1}>
                QR code is required
              </Typography>
            )}
            
            {formData.existingQrCode && !formData.qr_code && (
              <Box mt={2} display="flex" flexDirection="column" alignItems="center">
                <Typography variant="caption" color="text.secondary">
                  Current QR Code
                </Typography>
                <img 
                  src={formData.existingQrCode} 
                  alt="Bank QR Code" 
                  style={{ maxWidth: '200px', maxHeight: '200px', marginTop: '8px' }} 
                />
              </Box>
            )}
            
            {formData.qr_code && (
              <Box mt={2} display="flex" flexDirection="column" alignItems="center">
                <Typography variant="caption" color="text.secondary">
                  New QR Code Preview
                </Typography>
                <img 
                  src={URL.createObjectURL(formData.qr_code)} 
                  alt="QR Code Preview" 
                  style={{ maxWidth: '200px', maxHeight: '200px', marginTop: '8px' }} 
                />
              </Box>
            )}
          </Box>

          {submissionError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {submissionError}
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setModalOpen(false)} disabled={isSubmitting}>Cancel</Button>
        <Button 
          variant="contained" 
          onClick={handleSubmit}
          disabled={isSubmitting}
          startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
        >
          {isSubmitting ? "Saving..." : (account ? "Update" : "Save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddEditBankAccount;